import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Layout from './components/Layout';
import TemplateCreator from './pages/TemplateCreator';
import TemplateList from './pages/TemplateList';
import ConsentFiller from './pages/ConsentFiller';
import ConsentFillForm from './pages/ConsentFillForm';
import ConsentFormsList from './pages/ConsentFormsList';
import ConsentAudit from './pages/ConsentAudit';
import AdminRoles from './pages/AdminRoles';
import PatientHistory from './pages/PatientHistory';
import Dashboard from './pages/Dashboard';
import DataAuthorizationList from './pages/DataAuthorizationList';

// Componente para rutas protegidas
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/60">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Ruta solo para administradores
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/60">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/templates" replace />;
  }

  return <>{children}</>;
};

// Componente para rutas de login (redirigir si ya está autenticado)
const LoginRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/60">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando…</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="App">
          <Routes>
            {/* Ruta de login */}
            <Route 
              path="/login" 
              element={
                <LoginRoute>
                  <Login />
                </LoginRoute>
              } 
            />

            {/* Ruta protegida - Dashboard (redirige a plantillas) */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Navigate to="/templates" replace />
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Lista de Plantillas */}
            <Route 
              path="/templates" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <TemplateList />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Crear Plantilla */}
            <Route 
              path="/templates/create" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <TemplateCreator />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Editar Plantilla */}
            <Route 
              path="/templates/edit/:templateId" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <TemplateCreator />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Llenado de Consentimientos */}
            <Route 
              path="/consents" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ConsentFiller />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Llenar Consentimiento con Plantilla Específica */}
            <Route 
              path="/consents/fill/:templateId" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ConsentFillForm />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Consentimientos Guardados */}
            <Route 
              path="/consent-forms" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ConsentFormsList />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            <Route
              path="/autorizacion-datos"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DataAuthorizationList />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Ruta - Historial por paciente */}
            <Route 
              path="/historial-paciente" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <PatientHistory />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Estadísticas / Dashboard */}
            <Route 
              path="/estadisticas" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            {/* Ruta - Auditoría (solo administradores) */}
            <Route 
              path="/auditoria" 
              element={
                <AdminRoute>
                  <Layout>
                    <ConsentAudit />
                  </Layout>
                </AdminRoute>
              } 
            />

            {/* Ruta - Administrar permisos (solo administradores) */}
            <Route 
              path="/permisos" 
              element={
                <AdminRoute>
                  <Layout>
                    <AdminRoles />
                  </Layout>
                </AdminRoute>
              } 
            />

            {/* Ruta por defecto */}
            <Route path="/" element={<Navigate to="/templates" replace />} />
            
            {/* Ruta de fallback */}
            <Route path="*" element={<Navigate to="/templates" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
